"use client";
import { useState } from "react";
import {
  DateField as AriaDateField,
  DateFieldProps as AriaDateFieldProps,
  DateInput as AriaDateInput,
  DateInputProps,
  DateSegment,
  DateValue,
  Dialog,
  DialogTrigger,
  Popover,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { Button } from "../Button";
import { Calendar } from "../Calendar";
import { BaseInputProps, Description, FieldError, getInputStateStyles, InputWrapper, Label } from "../Field";
import { Icon } from "../Icon";

/**
 * DateField
 *
 * A segmented date input with optional calendar popup. Matches the API and styling
 * of TextField for consistency across form controls.
 */
export interface DateFieldProps<T extends DateValue>
  extends Omit<AriaDateFieldProps<T>, "isRequired" | "size" | "className">,
    Omit<BaseInputProps, "transparent" | "isClearable" | "onClear"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  tooltip?: string;
  isRequired?: boolean;
  className?: string;
  validationResult?: ValidationResult;
  /**
   * Whether to show a calendar button for date selection
   * @default true
   */
  showCalendar?: boolean;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default true
   */
  reserveErrorSpace?: boolean;
}

/**
 * Renders an Edges DateField with label, description, validation states,
 * and optional calendar picker.
 */
export function DateField<T extends DateValue>({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired,
  isDisabled,
  isInvalid,
  showCalendar = true,
  reserveErrorSpace = true,
  validationResult,
  className,
  ...props
}: DateFieldProps<T>) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleCalendarSelect = (date: DateValue | null) => {
    if (props.onChange && date) {
      // @ts-expect-error - Complex type mapping between DateValue and MappedDateValue<T>
      props.onChange(date);
    }
    setIsCalendarOpen(false);
  };

  return (
    <AriaDateField
      {...props}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isInvalid={isInvalid}
      className={composeTailwindRenderProps(className, "flex flex-col gap-1")}
    >
      {label && (
        <Label size={size} tooltip={tooltip} isRequired={isRequired}>
          {label}
        </Label>
      )}

      <InputWrapper>
        <div className="relative w-full">
          <DateInput size={size} isDisabled={isDisabled} isInvalid={isInvalid} hasCalendar={showCalendar} />
          {showCalendar && !isDisabled && (
            <DialogTrigger isOpen={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <Button
                variant="unstyled"
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded hover:bg-background-hover text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Open calendar"
              >
                <Icon name="Calendar" size={18} />
              </Button>
              <Popover className="bg-background-surface rounded-lg border border-border-default shadow-xl">
                <Dialog className="p-4 outline-none">
                  <Calendar
                    value={props.value}
                    onChange={handleCalendarSelect}
                    minValue={props.minValue}
                    maxValue={props.maxValue}
                    isDisabled={isDisabled}
                    isReadOnly={props.isReadOnly}
                    isDateUnavailable={props.isDateUnavailable}
                  />
                </Dialog>
              </Popover>
            </DialogTrigger>
          )}
        </div>
      </InputWrapper>

      {reserveErrorSpace ? (
        <div className="mt-1 min-h-[20px]">
          {typeof errorMessage === "string" ? (
            <FieldError size={size}>{errorMessage}</FieldError>
          ) : typeof errorMessage === "function" && validationResult ? (
            <FieldError size={size}>{errorMessage(validationResult)}</FieldError>
          ) : description ? (
            <Description size={size}>{description}</Description>
          ) : null}
        </div>
      ) : (
        <>
          {typeof errorMessage === "string" ? (
            <FieldError size={size} className="mt-1">
              {errorMessage}
            </FieldError>
          ) : typeof errorMessage === "function" && validationResult ? (
            <FieldError size={size} className="mt-1">
              {errorMessage(validationResult)}
            </FieldError>
          ) : description ? (
            <Description size={size} className="mt-1">
              {description}
            </Description>
          ) : null}
        </>
      )}
    </AriaDateField>
  );
}

function getSegmentStyles({
  isPlaceholder,
  isDisabled,
  size = "md",
}: {
  isPlaceholder?: boolean;
  isDisabled?: boolean;
  size?: Size;
}) {
  // Size styles with mobile font-size fix
  // iOS Safari zooms in on inputs with font-size < 16px when focused.
  // On mobile, we use text-base (16px) for sm/md sizes to prevent auto-zoom.
  // On desktop (md: breakpoint), we restore the original smaller sizes.
  const sizeStyles: Record<Size, string> = {
    sm: "text-base md:text-xs",
    md: "text-base md:text-sm",
    lg: "text-base",
    xl: "text-lg",
  };

  return twMerge(
    // Base
    "inline-block box-content px-0.5 rounded outline-none tabular-nums text-center transition-colors",
    sizeStyles[size],
    // Text color
    isPlaceholder ? "text-text-disabled italic" : "text-text-primary",
    // Disabled state
    isDisabled ? "text-text-disabled cursor-not-allowed" : "cursor-text",
    // Hover/focus
    !isDisabled && "hover:bg-background-hover",
    "data-[focused]:bg-background-selected data-[focused]:text-text-primary"
  );
}

interface DateInputComponentProps extends Omit<DateInputProps, "children"> {
  size?: Size;
  isDisabled?: boolean;
  isInvalid?: boolean;
  hasCalendar?: boolean;
}

export function DateInput({
  size = "md",
  isDisabled,
  isInvalid,
  hasCalendar,
  className,
  ...props
}: DateInputComponentProps) {
  return (
    <AriaDateInput
      className={twMerge(
        getInputStateStyles({
          isInvalid,
          isDisabled,
        }),
        // Padding adjustments
        "px-3 py-2 flex items-center gap-0.5 w-full",
        hasCalendar && "pr-10", // Make room for calendar icon
        className as string
      )}
      {...props}
    >
      {(segment) => (
        <DateSegment
          segment={segment}
          className={getSegmentStyles({
            isPlaceholder: segment.isPlaceholder,
            isDisabled: !segment.isEditable || isDisabled,
            size,
          })}
        />
      )}
    </AriaDateInput>
  );
}
